import { useState } from 'react';
import { Discount, useDiscountsModel } from '../model/Discounts.model';
import {
  Actions,
  Dispatchers,
  Option,
  toAsyncFetched,
} from '@client/new/helpers/types';
import { filterDiscounts, toDiscountRow, toOption } from './discounts.mappers';
import { ListRow } from '../view/components/GridList';
import { NewDiscountVM, useNewDiscountVM } from './newDiscount.viewModel';
import { toast } from 'react-hot-toast';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { z } from 'zod';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import { navigate, PATHS } from '@client/paths';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import { BundleStatus } from 'generated/graphql';
export type Filters = {
  status: Discount['status'] | 'All';
  type: Discount['product']['type'] | 'All';
};

export type DiscountsVM = {
  discounts: Array<Discount>;
  discountsCnt: number;
  newDiscount: NewDiscountVM;
  otps: Array<Option>;
  mode: 'list' | 'filling';
  deactivationMode:
    | { type: 'not-started' }
    | { type: 'confirmation'; value: number }
    | { type: 'in-progress' };
  confirmModalMode:
    | { type: 'not-started' }
    | { type: 'in-progress'; value: string };
  search: string;
  rows: ListRow[];
  filters: Filters;
  isCreatorLive: boolean;
} & Dispatchers<DiscountVMActionMap>;

export type DiscountVMActionMap = {
  ToggleModal: null;
  OpenConfirmModal: string;
  CloseConfirmModal: null;
  SetSearch: string;
  CloseDeactivating: null;
  CopyCode: string;
  StartDeactivateDiscount: number;
  FinishDeactivation: null;
  SetFilterStatus: Filters['status'];
  SetFilterType: Filters['type'];
  OpenDetails: string;
};

type DiscountVMAction = Actions<DiscountVMActionMap>;

export const useDiscountsVM = () => {
  const asyncModel = useDiscountsModel();
  const influencerId = useInfluencerId();

  const p = toQueryParamsSchema();

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState(p.search || '');
  const [filters, setFilters] = useState<DiscountsVM['filters']>({
    status: 'Active',
    type: 'All',
  });
  const [deactivationMode, setDeactivationMode] = useState<
    DiscountsVM['deactivationMode']
  >({ type: 'not-started' });

  const [confirmModalMode, setconfirmModalMode] = useState<
    DiscountsVM['confirmModalMode']
  >({ type: 'not-started' });

  const _dispatch = (a: DiscountVMAction) => {
    trackSessionEvent('ViewAction', a);
    switch (a.type) {
      case 'CopyCode': {
        navigator.clipboard.writeText(a.payload);
        toast.success('Copied');
        return;
      }
      case 'CloseDeactivating': {
        if (deactivationMode.type === 'confirmation')
          setDeactivationMode({ type: 'not-started' });
        return;
      }
      case 'StartDeactivateDiscount': {
        if (deactivationMode.type === 'not-started')
          setDeactivationMode({ type: 'confirmation', value: a.payload });
        return;
      }
      case 'FinishDeactivation': {
        if (
          asyncModel.type !== 'fetched' ||
          deactivationMode.type !== 'confirmation'
        )
          return;
        const discountId = deactivationMode.value;
        setDeactivationMode({ type: 'in-progress' });

        return asyncModel.value
          .deactivateDiscount(discountId)
          .then(() => toast.success('Discount was deactivated'))
          .catch((e) =>
            toast.success(
              'Cannot deactivate discount, please contact support:',
              e,
            ),
          )
          .finally(() => setDeactivationMode({ type: 'not-started' }));
      }

      case 'OpenConfirmModal':
        return setconfirmModalMode({ type: 'in-progress', value: a.payload });

      case 'CloseConfirmModal':
        return setconfirmModalMode({ type: 'not-started' });

      case 'ToggleModal':
        return setIsOpen((prev) => !prev);

      case 'SetSearch':
        return setSearch(a.payload);

      case 'SetFilterStatus':
        return setFilters((p) => ({ ...p, status: a.payload }));

      case 'SetFilterType':
        return setFilters((p) => ({ ...p, type: a.payload }));

      case 'OpenDetails':
        return navigate(a.payload);
    }
  };

  const dispatchers = getDispatchers<DiscountVMActionMap>(_dispatch);

  const newDiscount = useNewDiscountVM(asyncModel, dispatchers.dispatch);

  if (asyncModel.type !== 'fetched') return asyncModel;
  if (newDiscount.type !== 'fetched') return newDiscount;

  const model = asyncModel.value;

  const otpProgramsWithPrice = model.otps
    .filter((o) => o.type === 'published')
    .filter((o) => !!o.otpPrice)
    .map((v) => toOption(v.programId + '', v.title, 'PROGRAM'));

  const otpOffers = model.otpBundles
    .filter(
      (o) =>
        (o.oneTimeProduct?.price || 0) > 0 &&
        (o.status === BundleStatus.Draft ||
          o.status === BundleStatus.Published),
    )
    .map((v) => toOption(v.id + '', v.title, 'OFFER'));

  const otps = [...otpOffers, ...otpProgramsWithPrice];

  const discounts = filterDiscounts(model.discounts, search, filters)
    .sort((l, r) => r.createdAt - l.createdAt)
    .map((d) => ({
      ...d,
      detailLink: PATHS.PROMOTIONS_DETAILS(influencerId, d.discountId),
    }));

  const rows = discounts.map((d) =>
    toDiscountRow(d, dispatchers.dispatch, model.isCreatorLive),
  );

  return toAsyncFetched<DiscountsVM>({
    newDiscount: newDiscount.value,
    discounts,
    discountsCnt: model.discounts.length,
    confirmModalMode,
    otps,
    rows,
    search,
    deactivationMode,
    filters,
    isCreatorLive: model.isCreatorLive,
    mode: isOpen ? 'filling' : 'list',
    ...dispatchers,
  });
};

export const queryParamsSchema = z.object({
  programId: z.string().optional().nullish(),
  search: z.string().optional().nullish(),
  mode: z.enum(['list', 'create']).default('list'),
  offerId: z.string().optional().nullish(),
});

export type QueryParamsSchema = z.infer<typeof queryParamsSchema>;

export const toQueryParamsSchema = (s?: string): QueryParamsSchema => {
  try {
    const p = new URLSearchParams(s || window.location.search);
    return queryParamsSchema.parse({
      programId: p.get('programId'),
      search: p.get('search'),
      mode: p.get('mode') || 'list',
      offerId: p.get('offerId'),
    });
  } catch (e) {
    console.error('Cannot parse query params', e);
    return { mode: 'list' };
  }
};

const uppercaseFirstLetter = (v: string) =>
  v ? v.charAt(0).toUpperCase() + v.slice(1) : '';

const toType = (
  value: Filters['type'],
  name?: string,
): Option<Filters['type']> => ({
  value,
  label: name || uppercaseFirstLetter(value),
});
export const filterTypeOptions = [
  toType('All'),
  toType('subscription'),
  toType('otp', 'One-Time-Purchases'),
];
